import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import api from '../api';

const categories = [
  { value: 'school_fees', label: 'School Fees' },
  { value: 'extracurricular', label: 'Extracurricular Activities' },
  { value: 'other', label: 'Other' }
];

const ParentPaymentReceipt: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams<{ paymentId: string }>();
  const [payment, setPayment] = useState<any | null>(location.state?.payment || null);
  const [loading, setLoading] = useState(!location.state?.payment);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (location.state?.payment) {
      setPayment(location.state.payment);
      setLoading(false);
      setError(null);
      return;
    }

    const loadPayment = async () => {
      if (!paymentId) {
        setError('Receipt not found.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get('/parent-payments');
        const payments = Array.isArray(response.data) ? response.data : [];
        const selected = payments.find((item: any) => {
          const currentId = item._id || item.transactionId || item.createdAt || item.date;
          return String(currentId) === decodeURIComponent(paymentId);
        });

        if (!selected) {
          setError('Receipt not found for this payment.');
          setPayment(null);
          setLoading(false);
          return;
        }

        setPayment(selected);
        setError(null);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Unable to load this receipt.');
      } finally {
        setLoading(false);
      }
    };

    loadPayment();
  }, [location.state, paymentId]);

  const amountValue = useMemo(() => Number(payment?.amount || 0), [payment]);
  const categoryLabel = categories.find((opt) => opt.value === payment?.category)?.label || payment?.category || 'Other';
  const transactionId = payment?.transactionId || 'N/A';
  const paymentDate = payment ? new Date(payment.createdAt || payment.date).toLocaleString() : 'N/A';
  const paymentStatus = String(payment?.status || 'completed').toUpperCase();

  const printReceipt = () => {
    if (!payment) return;

    const receiptHtml = `
      <html>
        <head>
          <title>Payment Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
            h1 { font-size: 24px; margin-bottom: 16px; }
            .field { margin-bottom: 12px; }
            .label { font-weight: 700; display: inline-block; width: 140px; }
          </style>
        </head>
        <body>
          <h1>Payment Receipt</h1>
          <div class="field"><span class="label">Category:</span>${categoryLabel}</div>
          <div class="field"><span class="label">Amount:</span>₦${amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div class="field"><span class="label">Status:</span>${paymentStatus}</div>
          <div class="field"><span class="label">Transaction ID:</span>${transactionId}</div>
          ${payment.description ? `<div class="field"><span class="label">Description:</span>${payment.description}</div>` : ''}
          <div class="field"><span class="label">Date:</span>${paymentDate}</div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return;
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const downloadReceipt = () => {
    if (!payment) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFillColor(31, 62, 133);
    doc.rect(0, 0, pageWidth, 36, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('A3 Tech', 14, 23);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Parent Payment Receipt', pageWidth - 48, 23, { align: 'right' });

    doc.setTextColor(40, 40, 40);
    doc.setDrawColor(196, 196, 196);
    doc.setLineWidth(0.5);
    doc.line(14, 48, pageWidth - 14, 48);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Payment Confirmation', 14, 62);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const rows = [
      ['Receipt No.', transactionId],
      ['Category', categoryLabel],
      ['Status', paymentStatus],
      ['Date', paymentDate],
      ['Amount', `₦${amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      ['Description', payment.description || 'No description provided']
    ];

    let yPos = 78;
    rows.forEach(([label, value], index) => {
      const labelX = 18;
      const valueX = 78;
      doc.setFillColor(index % 2 === 0 ? 248 : 255, 249, 250);
      doc.rect(14, yPos - 5, pageWidth - 28, 14, 'F');
      doc.setDrawColor(228, 232, 240);
      doc.rect(14, yPos - 5, pageWidth - 28, 14, 'S');
      doc.setFont('helvetica', 'bold');
      doc.text(String(label), labelX, yPos + 2);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), valueX, yPos + 2);
      yPos += 16;
    });

    doc.setDrawColor(218, 221, 226);
    doc.line(14, yPos + 8, pageWidth - 14, yPos + 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(31, 62, 133);
    doc.text('Total Paid', 14, yPos + 24);
    doc.text(`₦${amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 40, yPos + 24, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 116, 122);
    doc.text('Thank you for paying with A3 Tech. This receipt confirms your successful payment.', 14, pageHeight - 24);
    doc.text('Generated on ' + new Date().toLocaleString(), pageWidth - 50, pageHeight - 24, { align: 'right' });

    doc.save(`parent-payment-receipt-${transactionId || 'receipt'}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, background: alpha(theme.palette.primary.main, 0.02), minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, maxWidth: 520, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Preparing receipt</Typography>
          <Typography color="text.secondary">Please wait while your payment details are loaded.</Typography>
        </Paper>
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Payment Receipt</Typography>
          <Alert severity="error" sx={{ mb: 3 }}>{error || 'Receipt not found.'}</Alert>
          <Button variant="contained" onClick={() => navigate('/parent/payments')}>
            Back to payments
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: alpha(theme.palette.primary.main, 0.02), minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden',
            background: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 4 },
              py: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: '#fff',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box>
                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.2 }}>A3 Tech</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Payment Receipt</Typography>
              </Box>
              <Chip
                label="Payment successful"
                sx={{
                  backgroundColor: alpha('#fff', 0.18),
                  color: '#fff',
                  fontWeight: 700,
                  border: '1px solid rgba(255,255,255,0.28)',
                  px: 1,
                }}
              />
            </Stack>
          </Box>

          <Card elevation={0} sx={{ borderRadius: 0, borderTop: 'none', background: 'transparent' }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      background: alpha(theme.palette.primary.main, 0.06),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
                      Total paid
                    </Typography>
                    <Typography variant="h3" sx={{ my: 2, fontWeight: 800, color: theme.palette.primary.main }}>
                      ₦{amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This receipt confirms your successful payment for {categoryLabel.toLowerCase()}.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box sx={{ p: 0 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Payment details</Typography>
                      <Button variant="outlined" onClick={() => navigate('/parent/payments')}>
                        Back to payments
                      </Button>
                    </Stack>

                    <Grid container spacing={2}>
                      {[
                        ['Category', categoryLabel],
                        ['Status', paymentStatus],
                        ['Transaction ID', transactionId],
                        ['Date', paymentDate],
                        ['Description', payment.description || 'No description provided'],
                      ].map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: theme.palette.background.default,
                              border: `1px solid ${theme.palette.divider}`,
                              height: '100%',
                            }}
                          >
                            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700, color: 'text.secondary' }}>
                              {label}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.8, fontWeight: 600, wordBreak: 'break-word' }}>
                              {String(value)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  A record of this payment has been added to your payment history.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button variant="outlined" onClick={printReceipt}>Print receipt</Button>
                  <Button variant="contained" onClick={downloadReceipt}>Download receipt</Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  );
};

export default ParentPaymentReceipt;
